
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Avatar,
  Divider,
  Badge,
  CircularProgress,
  Pagination,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import PersonIcon from "@mui/icons-material/Person";
import InvestmentDetail from "./InvestmentDetail";
import ApplicationModal from "./ApplicationModal";
import { commonRunQuery } from "../../util/API/loans";
import { decodeHtmlEntities } from "../../util/utilfunctions/utilFunctions";

const MOCK_INVESTOR_PRODUCTS = [
  {
    id: 201,
    title: "Private Tech Equity",
    minEntry: "$10,000",
    status: "Pending Review",
    creator: "John Doe",
  },
  {
    id: 202,
    title: "Family Office Debt",
    minEntry: "$100,000",
    status: "Active",
    creator: "Jane Smith",
  },
];

const CARDS_PER_PAGE = 6;

const decodeFileName = (name) => {
  if (!name) return "—";
  return name
    .replace(/&amp;#039;/g, "'")
    .replace(/&amp;#39;/g, "'")
    .replace(/&#039;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"');
};
const InvestmentMarketplace = ({ onBack, userRole }) => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [productToApply, setProductToApply] = useState(null);
  const [investmentsPlans, setInvestmentsPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Pagination state ──
  const [currentPage, setCurrentPage] = useState(1);

  const user = JSON.parse(sessionStorage.getItem("user")).payload?.body;

  const handleOpenApply = (product) => {
    setProductToApply(product);
    setIsApplyModalOpen(true);
  };

  const handleCloseApply = () => {
    setIsApplyModalOpen(false);
    setProductToApply(null);
  };

  // Reset to page 1 whenever tab changes
  const handleTabChange = (e, val) => {
    setTabValue(val);
    setCurrentPage(1);
  };

  useEffect(() => {
    getInvestments();
  }, []);

  const isAdmin = userRole === "admin";
  const getInvestments = async () => {
    setLoading(true);
    console.log("Enter the function to get investments");
    try {
      const response = await commonRunQuery(359, "");
      console.log("First Response ????????????", response);

      const records = response?.data?.body?.record;

      if (records) {
        const recordsArray = Array.isArray(records) ? records : [records];
        const mapped = recordsArray.map((item) => {
          const fund = item.funds;
          return {
            id: fund.id,
            title: decodeFileName(fund.name),
            minEntry: `$${Number(fund.minimum_ticket_size).toLocaleString()}`,
            status: "Active",
            creator: fund.created_by_role,
            description: fund.description,
            totalFundSize: `$${Number(fund.total_fund_size).toLocaleString()}`,
            targetMultiple: fund.target_multiple,
            targetYearlyReturn: `${fund.target_yearly_return}%`,
            tvpi: fund.tvpi,
            dpi: fund.dpi,
            rvpi: fund.rvpi,
            videoUrl: fund.associated_video_url,
            startDate: fund.start_date,
            endDate: fund.end_date,
          };
        });
        setInvestmentsPlans(mapped);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (selectedProduct) {
    return (
      <InvestmentDetail
        product={selectedProduct}
        onBack={() => setSelectedProduct(null)}
        userRole={userRole}
      />
    );
  }

  const globalFunds = investmentsPlans.filter(
  (p) => p.creator?.toLowerCase() === "admin"
);
const investorSubmissions = investmentsPlans.filter(
  (p) => p.creator?.toLowerCase() !== "admin"
);

const allProducts = tabValue === 0 ? globalFunds : investorSubmissions;

//  Use a single derived variable for both display and pagination
const activeList = userRole === 'admin' ? allProducts : investmentsPlans;
const totalPages = Math.ceil(activeList.length / CARDS_PER_PAGE);
const displayProducts = activeList.slice(
  (currentPage - 1) * CARDS_PER_PAGE,
  currentPage * CARDS_PER_PAGE
);

  // Slice the current page's worth of cards
  // const displayProducts = allProducts.slice(
  //   (currentPage - 1) * CARDS_PER_PAGE,
  //   currentPage * CARDS_PER_PAGE
  // );
//   if (userRole === 'admin') {
//     displayProducts =  allProducts.slice(
//     (currentPage - 1) * CARDS_PER_PAGE,
//     currentPage * CARDS_PER_PAGE
//   );
// } else {
//   displayProducts =  investmentsPlans.slice(
//     (currentPage - 1) * CARDS_PER_PAGE,
//     currentPage * CARDS_PER_PAGE
//   );
// }

  return (
    <Box sx={{ p: 4, maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 4, gap: 2 }}>
        <IconButton onClick={onBack} sx={{ bgcolor: "#fff", boxShadow: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" fontWeight="900" color="#002e4c">
            {isAdmin ? "Fund Management" : "Investment Marketplace"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {isAdmin
              ? "Oversee global offerings and investor-submitted funds"
              : "Explore and apply for new opportunities"}
          </Typography>
        </Box>
      </Box>

      {/* Navigation Tabs for Admin */}
      {isAdmin && (
        <Tabs
  value={tabValue}
  onChange={handleTabChange}
  sx={{ mb: 4, borderBottom: 1, borderColor: "divider" }}
>
  <Tab
    icon={<BusinessCenterIcon sx={{ fontSize: "1.2rem" }} />}
    iconPosition="start"
    label={
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        Global Funds
        <Chip
          label={globalFunds.length}
          size="small"
          sx={{ bgcolor: "#002e4c", color: "#fff", height: 18, fontSize: "0.7rem" }}
        />
      </Box>
    }
  />
  <Tab
    icon={<PersonIcon sx={{ fontSize: "1.2rem" }} />}
    iconPosition="start"
    label={
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        Investor Submissions
        <Chip
          label={investorSubmissions.length}
          size="small"
          sx={{ bgcolor: "#002e4c", color: "#fff", height: 18, fontSize: "0.7rem" }}
        />
      </Box>
    }
  />
</Tabs>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="textSecondary">
  Showing{" "}
  <strong>
    {Math.min((currentPage - 1) * CARDS_PER_PAGE + 1, activeList.length)}–
    {Math.min(currentPage * CARDS_PER_PAGE, activeList.length)}
  </strong>{" "}
  of <strong>{activeList.length}</strong> funds
</Typography>
      </Box>

      {/* Cards Grid */}
      <Grid container spacing={3}>
        {displayProducts.map((product) => (
          <Grid item xs={12} md={6} lg={4} key={product.id}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 3,
                transition: "0.3s",
                "&:hover": { transform: "translateY(-5px)", boxShadow: 6 },
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                  {isAdmin && (
                    <Typography
                      variant="caption"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      By: <strong>{product.creator}</strong>
                    </Typography>
                  )}
                </Box>

                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {product.title}
                </Typography>

                {product.description && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{
                      mb: 1,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {decodeHtmlEntities(product.description)}
                  </Typography>
                )}

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Minimum
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {product.minEntry}
                    </Typography>
                  </Box>
                  {product.targetYearlyReturn && (
                    <Box textAlign="right">
                      <Typography variant="caption" color="textSecondary">
                        Target Return
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" color="success.main">
                        {product.targetYearlyReturn}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {product.totalFundSize && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      Fund Size
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {product.totalFundSize}
                    </Typography>
                  </Box>
                )}
              </CardContent>

              <Box sx={{ p: 2, pt: 0 }}>
                {isAdmin ? (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button fullWidth variant="outlined" size="small">
                      Edit
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={() => setSelectedProduct(product)}
                    >
                      View
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => handleOpenApply(product)}
                      sx={{ bgcolor: "#002e4c", "&:hover": { bgcolor: "#004a7a" } }}
                    >
                      {userRole === "delegate" ? "Apply for Client" : "Apply Now"}
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={() => setSelectedProduct(product)}
                    >
                      View
                    </Button>
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Pagination Controls ── */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 4 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, value) => {
              setCurrentPage(value);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
            sx={{
              "& .MuiPaginationItem-root": {
                fontWeight: 500,
              },
              "& .Mui-selected": {
                backgroundColor: "#002e4c !important",
                color: "#fff",
              },
            }}
          />
        </Box>
      )}

      <ApplicationModal
        open={isApplyModalOpen}
        handleClose={handleCloseApply}
        product={productToApply}
        userRole={userRole}
        user={user}
      />
    </Box>
  );
};

export default InvestmentMarketplace;


